import { useQueryClient } from '@tanstack/react-query';
import { isRedirect, useRouter } from '@tanstack/react-router';
import { openRequestSink } from '@/api/interceptors';
import { authQueryKey } from '@/api/query-keys';
import { logoutUserMutationFn } from '@/api/services/iam';
import { clearSentryIdentity } from '@/helpers/errors/identity';
import { reportError } from '@/helpers/errors/report';
import { BASE_URL } from '@/helpers/url';
import { type LoginSearchSchema } from '@/router/utils';
import { useAuth0Context } from '@/routes/__root.hooks';

export type UseLogoutSearchSchema = Omit<LoginSearchSchema, 'aerr'> & {
  aerr: Required<LoginSearchSchema['aerr']>;
};

/**
 * Returns an auth logout() function that redirects to the login page with the specified search parameters.
 * Note: The reason nothing is memorized within this hook is because `auth.logout` issues a redirect.
 * Note: rotating tokens aren't invalidated by the SDK, and there is currently no way to do
 * it manually because they don't expose the `refresh_token` to the outside.
 * @see https://community.auth0.com/t/revoke-refresh-token-on-logout/80698
 */

export const useLogoutHandler = (search: UseLogoutSearchSchema) => {
  const router = useRouter();
  const { logout: authLogout } = useAuth0Context();
  const client = useQueryClient();

  return async function logout() {
    const { href: returnTo } = router.buildLocation({ to: '/login', search });
    const logoutParams = { returnTo: new URL(returnTo, BASE_URL).toString() };
    // Intercept any other requests (just in case).
    openRequestSink();

    // Ensure we have the latest auth token before logging out, in order to avoid abort issues,
    // since we're letting `logoutUserMutationFn` run in the background.
    try {
      await client.fetchQuery({ queryKey: authQueryKey, staleTime: 0 });
    } catch (e) {
      // Ignore errors while fetching the auth token. A redirect is the expected forced-logout signal.
      if (!isRedirect(e)) {
        reportError(e, { domain: 'auth', action: 'logout-token-refresh', level: 'info' });
      }
    }
    void logoutUserMutationFn(client);
    // Detach the Sentry identity so events after this point are not attributed to the
    // previous user (e.g. the next login on a shared browser).
    clearSentryIdentity();
    // Logout and redirect to login page
    return authLogout({ logoutParams });
  };
};
